<template>
  <v-form ref="form" v-show="show">
    <v-row class="pa-5" justify="space-around">
      <v-col cols="12">
        <h2 class="primary--text font-weight-bold pt-5 pl-2">
          {{
            edit ? $t('display/advert:edit.title-edit') + ': \'' + advert.id + '\'' : $t('display/advert:edit.title-add')
          }}
        </h2>

        <v-card elevation="0" flat tile>
          <v-card-text>
            <v-tabs
              color="primary"
              centered
              ref="tabs"
              v-model="currentTab"
              :show-arrows="true"
            >
              <v-tab v-show="edit">
                <v-icon class="mr-3">home</v-icon>
                {{ $t('display/advert:edit.labels.data') }}
              </v-tab>

              <v-tab v-show="edit" key="functions">
                <v-icon class="mr-3">settings_power</v-icon>
                {{ $t('display/advert:edit.labels.functions') }}
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="currentTab">
              <v-tab-item key="general" eager>
                <v-row justify="space-around">
                  <v-col cols="12" md="5">
                    <h4 class="primary--text font-weight-medium">
                      {{ $t('common:general') }}
                    </h4>

                    <!-- Name -->
                    <v-text-field
                      outlined
                      v-model="advert.name"
                      :label="$t('common:name')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                    />

                    <!-- Budget -->
                    <v-text-field
                      outlined
                      v-model="advert.budget"
                      :label="$t('common:budget')"
                      :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                    />

                    <v-row>
                      <v-col cols="11">
                        <v-text-field
                          outlined
                          v-model="advert.provision"
                          :label="$t('display/advert:edit.labels.provision')"
                          :rules="[ruleHundredPercent($t('utils:rules.percent100'))]"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <!-- Provision -->
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.provision') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <!-- Input Owner -->
                    <CustomerTypeAhead
                      ref="owner"
                      :value="advert.customer"
                      @input="advert.customer = $event"
                      :label="$t('common:customer')"
                    />

                    <v-row>
                      <v-col cols="11">
                        <!-- Tags -->
                        <TagComponent
                            :values="advert.tags"
                            @input="value => advert.tags = value"
                            :label="$t('common:tags')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.tags') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <!-- Black Tags -->
                    <v-row>
                      <v-col cols="11">
                        <TagComponent
                            :values="advert.blackTags"
                            @input="value => advert.blackTags = value"
                            :label="$t('common:black-tags')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.provision') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <div v-show="advert && advert.buttonContent !== 'BUTTON_CONTENT'">
                      <v-row>
                        <v-col cols="11">
                          <h4 class="primary--text font-weight-medium">
                            {{ $t('display/advert:edit.titles.brodcast-period') }}
                          </h4>
                        </v-col>
                        <v-col cols="1">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon v-on="on">help_outline</v-icon>
                            </template>
                            <span>{{ $t('display/advert:edit.description.broadcast-period') }}</span>
                          </v-tooltip>
                        </v-col>
                      </v-row>

                      <!-- Broadcast period -->
                      <DateField
                        ref="dateStart"
                        :key="dateStartKey"
                        @update="(v) => {advert.dateStart = v; $refs.dateEnd.setMinDate(v)}"
                        :isClearable="true"
                        :label="$t('common:start-date')"
                      />

                      <DateField
                        ref="dateEnd"
                        :key="dateEndKey"
                        @update="(v) => {advert.dateEnd = v; $refs.dateStart.setMaxDate(v)}"
                        :isClearable="true"
                        :label="$t('common:end-date') + $t('common:exclusive')"
                      />

                      <h4 class="primary--text font-weight-medium">
                        {{ $t('display/advert:edit.titles.brodcast-time') }}
                      </h4>

                      <!-- Broadcast time -->
                      <TimeField
                        clearable
                        ref="timeStart"
                        :isClearable="true"
                        @update="(v) => (advert.timeStart = v)"
                        :label="$t('display/advert:edit.labels.time-start')"
                      />

                      <TimeField
                        clearable
                        ref="timeEnd"
                        :isClearable="true"
                        @update="(v) => (advert.timeEnd = v)"
                        :label="$t('display/advert:edit.labels.time-end')"
                      />

                    </div>
                    
                    <RegionsComponent
                        ref="regionsComponent"
                        @regionUpdated="(v) => { advert.selectedRegions = v; }"
                    />

                  </v-col>
                  <v-col cols="12" md="5">
                    <h4 class="primary--text font-weight-medium">
                      {{ $t('display/advert:edit.titles.playback-option') }}
                    </h4>

                    <!-- Playback -->
                    <v-text-field
                      outlined
                      clearable
                      v-model="advert.mediaLength"
                      :label="$t('display/advert:edit.labels.playback-duration')"
                      :rules="[ruleRequired($t('utils:rules.required')), ruleNotNegative($t('utils:rules.negative-value'))]"
                    />

                    <v-row>
                      <v-col cols="11">
                        <!-- Budget type -->
                        <v-select
                          :items="budgetTypes"
                          item-text="translatedName"
                          item-value="id"
                          :menu-props="{bottom: true, offsetY: true}"
                          v-model="advert.budgetType"
                          outlined
                          clearable
                          :label="$t('display/advert:edit.labels.budget-type')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" class="cursor-pointer">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.budget') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <!-- ButtonContent -->
                    <v-row>
                      <v-col cols="11">
                        <v-select
                          :items="buttonContent"
                          item-text="translatedName"
                          item-value="id"
                          :menu-props="{bottom: true, offsetY: true}"
                          v-model="advert.buttonContent"
                          outlined
                          :label="$t('display/advert:edit.labels.button-content')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.button-content') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <!-- Excluded elevator -->
                    <v-row>
                      <v-col cols="11">
                        <v-autocomplete
                          clearable
                          hide-selected
                          multiple
                          outlined
                          persistent-hint
                          small-chips
                          :menu-props="{bottom: true, offsetY: true}"
                          :items="elevators"
                          item-text="name"
                          item-value="id"
                          v-model="advert.excludedElevator"
                          :label="$t('display/advert:edit.labels.excluded-elevator')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.excluded-elevator') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>

                    <!-- Specific elevator -->
                    <v-row>
                      <v-col cols="11">
                        <v-autocomplete
                          clearable
                          hide-selected
                          multiple
                          outlined
                          persistent-hint
                          small-chips
                          :menu-props="{bottom: true, offsetY: true}"
                          :items="elevators"
                          item-text="name"
                          item-value="id"
                          v-model="advert.specificElevator"
                          :label="$t('display/advert:edit.labels.specific-elevator')"
                        />
                      </v-col>
                      <v-col cols="1" style="padding-top: 30px">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on">help_outline</v-icon>
                          </template>
                          <span>{{ $t('display/advert:edit.description.specific-elevator') }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>


                    <!-- Advert Type -->
                    <v-select
                      clearable
                      :items="advertTypes"
                      item-text="translatedName"
                      item-value="id"
                      :menu-props="{bottom: true, offsetY: true}"
                      v-model="advertType"
                      @change="v => advert.advertType = v"
                      outlined
                      :label="$t('display/advert:edit.labels.advert-type')"
                    />

                    <v-row>
                      <v-col cols="12" md="3">

                        <!-- Fit screens -->
                        <v-checkbox
                          color="secondary"
                          v-model="advert.fitScreen"
                          :label="$t('display/advert:edit.labels.fit-screen')"
                          :disabled="isFitScreenDisable"
                        >
                          <template v-slot:label>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <div v-on="on">
                                  {{ $t('display/advert:edit.labels.fit-screen') }}
                                  <v-icon>help_outline</v-icon>
                                </div>
                              </template>
                              <span>{{ $t('display/advert:edit.description.fit-screen') }}</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>

                        <!-- Checkbox is Active -->
                        <v-checkbox
                          color="secondary"
                          v-model="advert.active"
                        >
                          <template v-slot:label>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <!-- Fit screens -->
                                <div v-on="on">
                                  {{ $t('common:active') }}
                                  <v-icon>help_outline</v-icon>
                                </div>
                              </template>
                              <span>{{ $t('display/advert:edit.description.active') }}</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>

                      <v-col cols="12" md="3">
                        <!-- Checkbox does visible for all -->
                        <v-checkbox
                          color="secondary"
                          v-model="advert.visibleAll"
                        >
                          <template v-slot:label>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <!-- Fit screens -->
                                <div v-on="on">
                                  {{ $t('display/advert:edit.labels.visible-all') }}
                                  <v-icon>help_outline</v-icon>
                                </div>
                              </template>
                              <span>{{ $t('display/advert:edit.description.visible-all') }}</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>

                        <!-- Soft deleted -->
                        <v-checkbox
                          color="secondary"
                          v-model="advert.deleted"
                        >
                          <template v-slot:label>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on }">
                                <!-- Fit screens -->
                                <div v-on="on">
                                  {{ $t('display/advert:edit.labels.deleted') }}
                                  <v-icon>help_outline</v-icon>
                                </div>
                              </template>
                              <span>{{ $t('display/advert:edit.description.deleted') }}</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>

                    <MediaContainer
                      v-show="advertType !== 'HTML'"
                      ref="media"
                      :mediaFile="advert.media"
                      :advertId="advert.id"
                      @newMedia="(v) => { advert.mediaId = v.id; advert.media = v; }"
                      @updateVideoLength="(v) => { advert.mediaLength = v }"
                      @updateType="(v) => { advert.advertType = v }"
                    />

                    <!-- website -->
                    <v-text-field
                      v-show="advertType === 'HTML'"
                      outlined
                      v-model="advert.filename"
                      :label="$t('display/advert:edit.labels.website')"
                    />

                    <v-row>
                      <v-tooltip class="w-10 p-3" bottom>
                        <template v-slot:activator="{ on }">
                          <v-col v-on="on" cols="4">
                            <div class="mb-1 font-weight-bold">Images</div>
                            <div>Size: 1280 x 720 Pixel</div>
                            <div>Resolution: 72dpi</div>
                            <div>Format: .jpg / .png</div>
                            <div>Color mode: RGB</div>
                            <div>Max. filesize: 200 MB</div>
                            <div>Automatic Compression</div>
                          </v-col>
                        </template>
                        <span>{{ $t('display/advert:edit.description.image-info') }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-col v-on="on" cols="4">
                            <div class="mb-1 font-weight-bold">Videos</div>
                            <div>Size: 1280 x 720 Pixel</div>
                            <div>Resolution: -</div>
                            <div>Framerate: 25fps</div>
                            <div>Format: .mp4 (h264 Codec)</div>
                            <div>Max. filesize: 200 MB</div>
                            <div>Automatic Compression</div>
                          </v-col>
                        </template>
                        <span>{{ $t('display/advert:edit.description.video-info') }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-col v-on="on" cols="4">
                            <div class="mb-1 font-weight-bold">PDF</div>
                            <div>Size: 1280 x 720 Pixel</div>
                            <div>Resolution: 72dpi</div>
                            <div>Format: .pdf (1st page only)</div>
                            <div>Color mode: RGB</div>
                            <div>Max. filesize: 200 MB</div>
                            <div>Preview: after compression
                              Automatic Compression
                            </div>
                          </v-col>
                        </template>
                        <span>{{ $t('display/advert:edit.description.pdf-info') }}</span>
                      </v-tooltip>
                    </v-row>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item v-if="edit" key="functions">
                <FunctionTab
                  :advert="advert"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import MediaContainer from '@/modules/display/views/advert/form/MediaContainer';
import CustomerTypeAhead from '@/modules/core/components/CustomerTypeAhead';
import ElevatorService from '@/modules/display/services/elevator-service';
import FunctionTab from '@/modules/display/views/advert/form/FunctionTab';
import RegionsComponent from '@/modules/display/views/advert/form/RegionsComponent';
import DateField from '@/modules/core/components/DateField';
import TimeField from '@/modules/core/components/TimeField';
import DateUtils from '@/modules/core/services/date-utils';
import EnumUtils from '@/modules/core/services/enum-utils';
import Utils from '@/modules/core/services/utils';
import TagComponent from '@/modules/core/components/TagComponent';
import {
  ruleRequired,
  ruleRequiredMultiple,
  ruleMaxFileSize,
  ruleNotNegative,
  ruleHundredPercent
} from '@/modules/core/services/rules';

export default {
  name: 'AdminForm',
  components: {
    MediaContainer,
    CustomerTypeAhead,
    FunctionTab,
    DateField,
    TimeField,
    RegionsComponent,
    TagComponent
  },
  props: {
    edit: Boolean
  },
  data() {
    return {
      advert: {},
      advertType: '',
      dateEndKey: 0,
      dateStartKey: 1,
      directions: [],
      budgetTypes: [],
      buttonContent: [],
      elevators: [],
      advertTypes: [],
      currentTab: null,
      show: false
    }
  },
  computed: {
    isFitScreenDisable() {
      if (!this.advert || !this.advert.media || !this.advert.media.id) {
        return false;
      }

      return this.advert.media.active === 'ACTIVE';
    }
  },
  methods: {
    ruleRequired,
    ruleRequiredMultiple,
    ruleMaxFileSize,
    ruleNotNegative,
    ruleHundredPercent,

    initialize(advert) {
      this.advert = advert;

      //always initialize with "ALL"
      this.advert.advertDirection = "ALL";

      //make copy of variable because in case when advertType is HTML,
      // the value is transformed to low case, after the component is rendered #LIFT-271
      this.advertType = (' ' + this.advert.advertType).slice(1);

      this.$refs.regionsComponent.init(this.advert.regions);
      this.$refs.owner.initialise(this.advert.customer);

      let dateStart = DateUtils.parseStringToDate(this.advert.dateStart);
      let dateEnd = DateUtils.parseStringToDate(this.advert.dateEnd);

      if (dateStart > dateEnd) {
        this.advert.dateEnd = this.advert.dateStart;
      }

      if (this.advert.dateStart) {
        this.advert.dateStart = this.$refs.dateStart.parseDTO(this.advert.dateStart);
        this.$refs.dateEnd.setMinDate(this.advert.dateStart);
      }

      if (this.advert.dateEnd) {
        this.advert.dateEnd = this.$refs.dateEnd.parseDTO(this.advert.dateEnd);
        this.$refs.dateStart.setMaxDate(this.advert.dateEnd);
      }

      if (this.advert.timeStart) {
        this.advert.timeStart = this.$refs.timeStart.parseDTO(this.advert.timeStart);
      }

      if (this.advert.timeEnd) {
        this.advert.timeEnd = this.$refs.timeEnd.parseDTO(this.advert.timeEnd);
      }
      this.show = true;
    },

    loadAdvertTypes() {
      EnumUtils.loadEnum('advertType', 'display/advert', 'advertType',
        (response) => {
          this.advertTypes = response;
        }
      );
    },

    loadBudgetType() {
      EnumUtils.loadEnum('budgetTypes', 'display/advert', 'budgetTypes',
        (response) => {
          this.budgetTypes = response;
        }
      );
    },

    loadButtonContent() {
      EnumUtils.loadEnum('buttonContent', 'display/advert', 'buttonContent',
        (response) => {
          this.buttonContent = response;
        }
      );
    },

    loadAllElevators() {
      ElevatorService.readAllIdName(
        (response) => {
          for (var i = 0; i < response.data.length; i++) {
            this.elevators.push({id: response.data[i][0], name: response.data[i][1]});
          }
        },
        (error) => {
          this.$logger.error("Error at InfoService.readEnum: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:elevator_plural')}));
        });

    },
    resetDefaultData() {
      this.$refs.timeEnd.resetData();
      this.$refs.timeStart.resetData();
    },

    reset() {
      /**
       * There is a little bug in DateField. It uses the v-mask library, and this library is a cause of the
       * problem when popup window is opened, several times without the page reloading.
       * It is set for DateField components the corresponding key, and before the popup window is closed, it is changing the
       * key. It means that Vuejs should umount the "old" component and create the new one.
       */
      this.dateStartKey++;
      this.dateEndKey--;
      this.$refs.form.reset();
      this.$refs.media.reset();
      this.currentTab = null;
    },

    validate() {
      return this.$refs.form.validate();
    }
  },
  created() {
    this.loadButtonContent();
    this.loadBudgetType();
    this.loadAllElevators();
    this.loadAdvertTypes();
  }
}

</script>
